// The inspector: what the selected block is, why it exists, its fields, its bits and its bytes.
import { store, type State } from '../state';
import { fieldsOf, hasKids, kids, timeOf, type Model } from '../model';
import type { Field, Segment } from '../parsers/types';
import { h, hideTip, richText, showTip, toast } from './dom';
import { fieldColor, theme } from './theme';
import { fmtBytes, plural } from '../parsers/bits';
import { KIND_HELP, KIND_LABEL } from '../content/kinds';
import { fmtTime, linksOf, playTarget, reveal, zoomToSelection } from './anatomy';
import { renderViews } from './codecviews';

const HEX_PAGE = 512;

export class Inspector {
  readonly el: HTMLElement;
  private body: HTMLElement;
  private hexLimit = HEX_PAGE;
  /** Items that can be highlighted: fields and (for fieldless ranges) child segments. */
  private marks: { f: Field; color: string; child?: Segment }[] = [];
  private hl: number | null = null;
  private onStep: (dir: 1 | -1) => void = () => {};

  constructor(private m: Model) {
    this.body = h('div', {});
    this.el = h('section', { class: 'card inspector' }, this.body);
    store.sub((s, p) => this.onState(s, p));
    this.renderEmpty();
  }

  setStepHandler(fn: (dir: 1 | -1) => void) {
    this.onStep = fn;
  }

  private onState(s: State, p: State) {
    if (s.sel === p.sel) return;
    this.hexLimit = HEX_PAGE;
    if (!s.sel) this.renderEmpty();
    else if (s.sel.kind === 'seg') this.renderSeg(s.sel.seg);
    else if (s.sel.kind === 'time') this.renderTime(s.sel.t0, s.sel.t1);
  }

  private renderEmpty() {
    this.body.replaceChildren(
      h('div', { class: 'insp-empty' },
        h('div', { style: { fontSize: '15px', color: 'var(--text)', marginBottom: '6px', fontWeight: '600' } }, 'Pick any block to look inside it.'),
        h('div', {}, 'Click the byte map, a row in the structure list, or a point in the sound. Drag across the sound to select a stretch of time.'),
      ),
    );
  }

  // ------------------------------------------------------------------ time range
  private renderTime(t0: number, t1: number) {
    const m = this.m;
    const br = m.bytesForTime(t0, t1);
    const i0 = Math.max(0, m.frameIndexAtTime(t0)), i1 = Math.max(0, m.frameIndexAtTime(t1));
    const dur = t1 - t0;
    const n = br ? br[1] - br[0] : 0;
    const kbps = dur > 0 ? (n * 8) / dur / 1000 : 0;
    const blocks = m.frames.length > 1 ? i1 - i0 + 1 : 0;
    const blockName = m.frames[0]?.name.replace(/\s*\d+.*$/, '').toLowerCase() || 'block';
    const text =
      `These ${fmtDur(dur)} of sound come from **${fmtBytes(Math.round(n))}** of the file` +
      (blocks ? `, spread over **${plural(blocks, blockName)}**` : '') +
      `. That is about **${Math.round(kbps).toLocaleString('en-US')} kbps**` +
      (m.pf.audio.bitrate ? `, compared with ${Math.round(m.pf.audio.bitrate / 1000)} kbps for the whole file.` : '.');
    const playBtn = h('button', { class: 'btn primary' }, '▶ Play selection');
    playBtn.onclick = () => playTarget({ kind: 'time', t0, t1 });
    const zoom = h('button', { class: 'btn' }, 'Zoom to selection');
    zoom.onclick = () => zoomToSelection();
    this.body.replaceChildren(
      h('div', { class: 'insp-head' },
        h('div', { class: 'insp-title' }, h('h2', {}, 'Time selection')),
        h('div', { class: 'insp-meta' },
          h('span', {}, 'from ', h('b', {}, fmtTime(t0, dur))), h('span', {}, 'to ', h('b', {}, fmtTime(t1, dur))),
          h('span', {}, h('b', {}, fmtDur(dur))),
          br ? h('span', {}, 'bytes ', h('b', {}, `${Math.floor(br[0]).toLocaleString('en-US')}–${Math.ceil(br[1]).toLocaleString('en-US')}`)) : null),
        h('div', { class: 'insp-actions' }, playBtn, zoom)),
      h('div', { class: 'explain', html: `<p>${richText(text)}</p>` }),
    );
  }

  // ------------------------------------------------------------------ segment
  private renderSeg(s: Segment) {
    this.hl = null;
    const m = this.m, T = theme();
    const path = m.pathTo(s) ?? [s];
    const ex = m.explain(s.type);
    const tr = timeOf(m, s);
    const size = s.end - s.start;

    const crumbs = h('div', { class: 'crumbs' });
    path.slice(0, -1).forEach((p) => {
      const b = h('button', {}, p.name);
      b.onclick = () => { store.set({ sel: { kind: 'seg', seg: p } }); reveal({ kind: 'seg', seg: p }); };
      crumbs.append(b, h('span', {}, '›'));
    });

    const kindTag = h('span', { class: 'kind', style: { background: T.kind[s.kind] }, title: KIND_HELP[s.kind] }, KIND_LABEL[s.kind]);
    const meta = h('div', { class: 'insp-meta' },
      h('span', {}, 'offset ', h('b', {}, `${s.start.toLocaleString('en-US')}`), ` (0x${s.start.toString(16).toUpperCase()})`),
      h('span', {}, 'size ', h('b', {}, `${size.toLocaleString('en-US')} bytes`)),
      h('span', {}, h('b', {}, pct(size, m.pf.size)), ' of file'),
      tr ? h('span', {}, 'plays ', h('b', {}, `${fmtTime(tr[0], tr[1] - tr[0])} → ${fmtTime(tr[1], tr[1] - tr[0])}`), ` (${fmtDur(tr[1] - tr[0])})`) : null,
    );
    const actions = h('div', { class: 'insp-actions' });
    if (tr && store.s.player) {
      const b = h('button', { class: 'btn primary', title: 'Play this block (Space)' }, '▶ Play this block');
      b.onclick = () => playTarget({ kind: 'seg', seg: s });
      actions.append(b);
    }
    const parentList = path.length > 1 ? kids(path[path.length - 2]) : m.top;
    if (parentList.length > 1) {
      const prev = h('button', { class: 'btn', title: 'Previous block (←)' }, '← Prev');
      const next = h('button', { class: 'btn', title: 'Next block (→)' }, 'Next →');
      prev.onclick = () => this.onStep(-1);
      next.onclick = () => this.onStep(1);
      actions.append(prev, next);
    }
    const zoom = h('button', { class: 'btn', title: 'Zoom both views to this block (Z)' }, 'Zoom to');
    zoom.onclick = () => zoomToSelection();
    actions.append(zoom);
    if (s.image) {
      const dl = h('button', { class: 'btn' }, 'Save image');
      dl.onclick = () => download(m.bytes.slice(s.image!.start, s.image!.end), `${m.name.replace(/\.[^.]+$/, '')}-cover.${extFor(s.image!.mime)}`, s.image!.mime);
      actions.append(dl);
    }

    const parts: (HTMLElement | null)[] = [
      h('div', { class: 'insp-head' }, crumbs, h('div', { class: 'insp-title' }, h('h2', {}, s.name), kindTag), s.summary ? h('div', { style: { color: 'var(--text2)', marginTop: '2px' } }, s.summary) : null, meta, actions),
      s.warning ? h('div', { class: 'warnbox' }, '▲ ' + s.warning) : null,
      h('div', { class: 'explain', html: ex ? `<h3>${richText(ex.title)}</h3><p>${richText(ex.body)}</p>` : `<p>${richText(KIND_HELP[s.kind])}</p>` }),
    ];

    if (s.image) {
      const blob = new Blob([m.bytes.slice(s.image.start, s.image.end)], { type: s.image.mime || 'image/jpeg' });
      const img = h('img', { class: 'imgprev', alt: 'Embedded image', src: URL.createObjectURL(blob) });
      img.onerror = () => img.replaceWith(h('div', { class: 'dim' }, 'The browser cannot show this image.'));
      parts.push(h('div', { class: 'sec' }, h('div', { class: 'sec-h' }, 'Picture'), img));
    }

    const fields = fieldsOf(s);
    this.marks = fields.map((f, i) => ({ f, color: fieldColor(i) }));
    // Without fields, color the hex by child blocks instead, so the structure still shows.
    if (!fields.length && hasKids(s)) {
      const ch = kids(s).slice(0, 400);
      this.marks = ch.map((c, i) => ({
        f: { name: c.name, value: c.summary ?? fmtBytes(c.end - c.start), bit: c.start * 8, bits: (c.end - c.start) * 8 },
        color: fieldColor(i),
        child: c,
      }));
    }

    // Inside the audio: what the codec stored in this frame.
    const codec = m.codec;
    const fi = codec ? m.frameIndexOf(s) : -1;
    if (codec && fi >= 0) {
      const sec = h('div', { class: 'sec' }, h('div', { class: 'sec-h' }, 'Inside the audio', h('span', { class: 'spacer' }), h('span', {}, `${codec.codec} · ${m.frames[fi].name}`)));
      try {
        const t = performance.now();
        const views = codec.detail(fi);
        if (performance.now() - t > 50) console.info(`codec detail took ${(performance.now() - t).toFixed(0)} ms`);
        sec.append(renderViews(views));
      } catch (e) {
        sec.append(h('div', { class: 'warnbox', style: { margin: '0' } }, `Could not decode this frame: ${(e as Error).message}`));
      }
      parts.push(sec);
    }

    const links = linksOf(s);
    if (links.length) {
      const list = h('div', { class: 'linklist' });
      for (const l of links) {
        const btn = h('button', { class: 'btn', title: 'Select the block that holds these bytes' }, `${l.label} · ${fmtBytes(l.end - l.start)} @ ${l.start.toLocaleString('en-US')}`);
        btn.onclick = () => {
          const path = m.segmentAt(l.start, 1e9, 0);
          const target = [...path].reverse().find((x) => x.start <= l.start && l.end <= x.end) ?? path[path.length - 1];
          if (target) { store.set({ sel: { kind: 'seg', seg: target } }); reveal({ kind: 'seg', seg: target }); }
        };
        list.append(btn);
      }
      parts.push(h('div', { class: 'sec' }, h('div', { class: 'sec-h' }, 'Linked bytes', h('span', { class: 'spacer' }), h('span', {}, 'drawn as orange arcs above the byte map')), list));
    }

    const bitSec = this.bitDiagram(s);
    if (bitSec) parts.push(bitSec);
    if (fields.length) parts.push(this.fieldTable(fields));
    else if (hasKids(s)) parts.push(this.childTable(s));
    parts.push(this.hexSection(s));
    this.body.replaceChildren(...parts.filter(Boolean) as HTMLElement[]);
  }

  private fieldTable(fields: Field[]): HTMLElement {
    const tb = h('tbody', {});
    fields.forEach((f, i) => {
      const color = this.marks[i].color;
      const tr = h('tr', { 'data-i': String(i), style: { '--fc': color } as unknown as Record<string, string> },
        h('td', { class: 'fn' }, h('span', { class: 'fsw', style: { background: color } }), f.name),
        h('td', { class: 'fv' }, f.value, f.note ? h('div', { class: 'note' }, f.note) : null),
        h('td', { class: 'fo', title: 'Position of this field in the file' }, locLabel(f)),
      );
      tr.style.setProperty('--fc', color);
      tr.addEventListener('pointerenter', () => this.highlight(i));
      tr.addEventListener('pointerleave', () => this.highlight(null));
      tb.append(tr);
    });
    return h('div', { class: 'sec' }, h('div', { class: 'sec-h' }, 'Fields', h('span', { class: 'spacer' }), h('span', {}, plural(fields.length, 'field'))), h('table', { class: 'fields' }, tb));
  }

  private childTable(s: Segment): HTMLElement {
    const all = kids(s);
    const ch = all.slice(0, 200);
    const tb = h('tbody', {});
    ch.forEach((c, i) => {
      const color = this.marks[i]?.color ?? theme().kind[c.kind];
      const tr = h('tr', { style: { cursor: 'pointer' } },
        h('td', { class: 'fn' }, h('span', { class: 'fsw', style: { background: color } }), c.name),
        h('td', { class: 'fv' }, c.summary ?? '', h('div', { class: 'note' }, KIND_LABEL[c.kind])),
        h('td', { class: 'fo' }, fmtBytes(c.end - c.start)),
      );
      tr.style.setProperty('--fc', color);
      tr.addEventListener('pointerenter', () => { this.highlight(i); store.set({ hover: { kind: 'seg', seg: c } }); });
      tr.addEventListener('pointerleave', () => { this.highlight(null); store.set({ hover: null }); });
      tr.addEventListener('click', () => { store.set({ sel: { kind: 'seg', seg: c } }); reveal({ kind: 'seg', seg: c }); });
      tb.append(tr);
    });
    return h('div', { class: 'sec' },
      h('div', { class: 'sec-h' }, 'Contains', h('span', { class: 'spacer' }), h('span', {}, plural(all.length, 'block') + (all.length > ch.length ? ` (first ${ch.length} shown)` : ''))),
      h('table', { class: 'fields' }, tb));
  }

  /** Bit-level diagram for packed headers (fields that don't start or end on byte boundaries). */
  private bitDiagram(s: Segment): HTMLElement | null {
    const fields = fieldsOf(s);
    const packed = fields.filter((f) => f.bit % 8 !== 0 || f.bits % 8 !== 0);
    if (!packed.length) return null;
    // Bit range to show: from the first packed field's byte to the last, max 16 bytes, rows of 32 bits.
    const lo = Math.max(s.start * 8, Math.floor(Math.min(...packed.map((f) => f.bit)) / 8) * 8);
    let hi = Math.max(...packed.map((f) => f.bit + f.bits));
    hi = Math.min(hi, lo + 128);
    hi = lo + Math.ceil((hi - lo) / 32) * 32;
    const b = this.m.bytes;
    const wrap = h('div', { class: 'bits' });
    for (let row = lo; row < hi; row += 32) {
      const grid = h('div', { class: 'bitrow' });
      grid.append(h('span', { class: 'off', style: { gridRow: '1' } }, `0x${(row >> 3).toString(16).toUpperCase()}`));
      for (let k = 0; k < 32; k++) {
        const p = row + k;
        const byte = p < s.end * 8 ? b[p >> 3] : undefined;
        const bit = byte === undefined ? '' : String((byte >> (7 - (p & 7))) & 1);
        const fi = fields.findIndex((f) => p >= f.bit && p < f.bit + f.bits);
        const el = h('span', { class: `bit${k % 8 === 0 ? ' b0' : ''}`, style: { gridColumn: String(k + 2), gridRow: '1' }, 'data-fi': String(fi) }, bit);
        if (fi >= 0) {
          el.style.background = `color-mix(in srgb, ${this.marks[fi].color} 30%, var(--panel2))`;
          el.addEventListener('pointerenter', () => this.highlight(fi));
          el.addEventListener('pointerleave', () => this.highlight(null));
        }
        grid.append(el);
      }
      // Labels under each field's span of bits.
      fields.forEach((f, fi) => {
        const a = Math.max(row, f.bit), z = Math.min(row + 32, f.bit + f.bits);
        if (z <= a) return;
        // Several fields can describe the same bits (e.g. Opus "config" → mode, bandwidth, duration).
        // Label only the first; the others stay in the table and highlight the same bits.
        if (fields.some((g, gi) => gi < fi && g.bit <= f.bit && g.bit + g.bits >= f.bit + f.bits)) return;
        const lab = h('span', { class: 'bitlab', 'data-fi': String(fi), style: { gridColumn: `${a - row + 2} / ${z - row + 2}`, '--fc': this.marks[fi].color } as unknown as Record<string, string> }, f.name);
        lab.style.setProperty('--fc', this.marks[fi].color);
        lab.title = `${f.name}: ${f.value}`;
        lab.addEventListener('pointerenter', (e) => { this.highlight(fi); showTip(`<b>${richText(f.name)}</b> = <span class="mono">${richText(f.value)}</span>${f.note ? `<br>${richText(f.note)}` : ''}`, e.clientX, e.clientY); });
        lab.addEventListener('pointerleave', () => { this.highlight(null); hideTip(); });
        grid.append(lab);
      });
      wrap.append(grid);
    }
    return h('div', { class: 'sec' }, h('div', { class: 'sec-h' }, 'Bits', h('span', { class: 'spacer' }), h('span', {}, 'each square is one bit')), wrap);
  }

  private hexSection(s: Segment): HTMLElement {
    const sec = h('div', { class: 'sec' });
    const draw = () => {
      const b = this.m.bytes;
      const start = s.start;
      const end = Math.min(s.end, s.start + this.hexLimit);
      const rowStart = start & ~15;
      // Map each byte to the mark covering it.
      const owner = new Int32Array(end - rowStart).fill(-1);
      this.marks.forEach((mk, i) => {
        const a = Math.max(rowStart, mk.f.bit >> 3), z = Math.min(end, Math.ceil((mk.f.bit + mk.f.bits) / 8));
        for (let p = a; p < z; p++) if (owner[p - rowStart] < 0) owner[p - rowStart] = i;
      });
      const hex = h('div', { class: 'hex' });
      for (let r = rowStart; r < end; r += 16) {
        const hx = h('span', { class: 'h' }), as = h('span', { class: 'a' });
        for (let k = 0; k < 16; k++) {
          const p = r + k;
          const inSeg = p >= start && p < end;
          const v = b[p];
          const cls = !inSeg || v === undefined ? 'out' : '';
          const o = inSeg ? owner[p - rowStart] : -1;
          const hs = h('span', { class: cls + (o >= 0 ? ' f' : ''), 'data-p': String(p) }, v === undefined || !inSeg ? '  ' : v.toString(16).padStart(2, '0').toUpperCase());
          const ch = v >= 32 && v < 127 ? String.fromCharCode(v) : '.';
          const asp = h('span', { class: cls + (o >= 0 ? ' f' : ''), 'data-p': String(p) }, !inSeg || v === undefined ? ' ' : ch);
          if (o >= 0) {
            hs.style.setProperty('--fc', this.marks[o].color);
            asp.style.setProperty('--fc', this.marks[o].color);
            hs.dataset.fi = asp.dataset.fi = String(o);
          }
          hx.append(hs);
          as.append(asp);
        }
        hex.append(h('div', { class: 'hexrow' }, h('span', { class: 'o' }, r.toString(16).toUpperCase().padStart(8, '0')), hx, as));
      }
      hex.addEventListener('pointerover', (e) => {
        const t = e.target as HTMLElement;
        const fi = t.dataset.fi;
        if (fi === undefined) { this.highlight(null); hideTip(); return; }
        const i = +fi;
        this.highlight(i);
        const mk = this.marks[i];
        const p = +(t.dataset.p ?? 0);
        showTip(`<b>${richText(mk.f.name)}</b><br><span class="mono">${richText(mk.f.value)}</span><div class="tt-k mono">byte ${p.toLocaleString('en-US')} = 0x${(b[p] ?? 0).toString(16).toUpperCase().padStart(2, '0')} = ${(b[p] ?? 0).toString(2).padStart(8, '0')}b</div>`, e.clientX, e.clientY);
      });
      hex.addEventListener('pointerleave', () => { this.highlight(null); hideTip(); });
      hex.addEventListener('click', (e) => {
        const fi = (e.target as HTMLElement).dataset.fi;
        const child = fi !== undefined ? this.marks[+fi].child : undefined;
        if (child) { store.set({ sel: { kind: 'seg', seg: child } }); reveal({ kind: 'seg', seg: child }); }
      });
      const shown = end - start;
      const more = s.end - end;
      const head = h('div', { class: 'sec-h' }, 'Bytes', h('span', { class: 'spacer' }), h('span', {}, more > 0 ? `first ${shown.toLocaleString('en-US')} of ${(s.end - s.start).toLocaleString('en-US')}` : plural(shown, 'byte')));
      const kids_: HTMLElement[] = [head, hex];
      if (more > 0) {
        const btn = h('button', { class: 'btn hex-more' }, `Show ${Math.min(more, this.hexLimit * 3).toLocaleString('en-US')} more bytes`);
        btn.onclick = () => { this.hexLimit *= 4; draw(); };
        kids_.push(btn);
      }
      if (s.end - s.start <= 4096) {
        const copy = h('button', { class: 'btn hex-more', style: { marginLeft: '8px' } }, 'Copy hex');
        copy.onclick = async () => {
          const txt = Array.from(b.subarray(s.start, s.end), (x) => x.toString(16).padStart(2, '0')).join(' ');
          await navigator.clipboard.writeText(txt);
          toast('Hex copied');
        };
        kids_.push(copy);
      }
      sec.replaceChildren(...kids_);
      if (this.hl !== null) this.highlight(this.hl);
    };
    draw();
    return sec;
  }

  private highlight(i: number | null) {
    this.hl = i;
    this.el.querySelectorAll('.hl').forEach((e) => e.classList.remove('hl'));
    if (i === null) return;
    this.el.querySelectorAll(`[data-fi="${i}"], tr[data-i="${i}"]`).forEach((e) => e.classList.add('hl'));
    if (this.marks[i]?.child === undefined) {
      const rows = this.el.querySelectorAll('table.fields tbody tr');
      rows[i]?.classList.add('hl');
    }
  }
}

const pct = (a: number, b: number) => {
  const p = (a / Math.max(1, b)) * 100;
  return p >= 10 ? `${p.toFixed(0)}%` : p >= 0.1 ? `${p.toFixed(1)}%` : p > 0 ? '<0.1%' : '0%';
};

function fmtDur(d: number) {
  if (d < 1) return `${(d * 1000).toFixed(d < 0.01 ? 2 : 1)} ms`;
  if (d < 60) return `${d.toFixed(2)} s`;
  return fmtTime(d, d);
}

function locLabel(f: Field): string {
  const byte = f.bit >> 3;
  if (f.bit % 8 === 0 && f.bits % 8 === 0) return `@${byte.toLocaleString('en-US')} · ${f.bits / 8}B`;
  return `@${byte.toLocaleString('en-US')}.${f.bit % 8} · ${f.bits}b`;
}

export function download(data: Uint8Array | string, name: string, mime: string) {
  const blob = new Blob([data as BlobPart], { type: mime });
  const a = h('a', { href: URL.createObjectURL(blob), download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function extFor(mime: string) {
  return mime.includes('png') ? 'png' : mime.includes('gif') ? 'gif' : mime.includes('webp') ? 'webp' : 'jpg';
}
